<template>
  <div class="article-container">
    <!-- 雪花效果 -->
    <div class="snowflakes" aria-hidden="true">
      <div v-for="n in 50" :key="n" class="snowflake">❅</div>
    </div>
    
    <!-- 移动端汉堡菜单 -->
    <div class="mobile-nav-toggle" @click="toggleNav">
      <div class="hamburger" :class="{ 'active': isNavOpen }">
        <span></span>
        <span></span>
        <span></span>
      </div>
    </div>

    <!-- 导航栏 -->
    <nav class="nav-bar" :class="{ 'nav-open': isNavOpen }">
      <div class="terminal-header">
        <div class="terminal-buttons">
          <span></span>
          <span></span>
          <span></span>
        </div>
        <div class="terminal-title">csoulkey@portfolio:~$</div>
      </div>
      <div class="nav-links">
        <router-link to="/#hero" @click="closeNav">cd ~/home</router-link>
        <router-link to="/#about" @click="closeNav">cd ~/about</router-link>
        <router-link to="/#projects" @click="closeNav">cd ~/projects</router-link>
        <router-link to="/#music" @click="closeNav">cd ~/music</router-link>
        <router-link to="/#articles" @click="closeNav">cd ~/articles</router-link>
        <router-link to="/#contact" @click="closeNav">cd ~/contact</router-link>
      </div>
    </nav>

    <div class="back-button">
      <router-link to="/" class="back-link">
        <i class="fas fa-arrow-left"></i> 返回首页
      </router-link>
    </div>

    <div class="article-detail">
      <div class="article-header">
        <div class="article-meta">
          <span class="article-date">2024.03.15</span>
          <div class="article-tags">
            <span>#AI</span>
            <span>#音乐科技</span>
            <span>#开发笔记</span>
          </div>
        </div>
        <h1 class="article-title">构建下一代AI音乐平台</h1>
        <div class="article-author">
          <img src="../assets/avatar.png" alt="CSOULKEY" class="author-avatar">
          <span class="author-name">CSoulKey</span>
        </div>
      </div>

      <div class="article-content">
        <p>在人工智能快速发展的今天，AI音乐生成技术正在改变着音乐创作的方式。作为一名深耕AI音乐领域的开发者，我想分享一下在构建下一代AI音乐平台过程中的心得与见解。</p>

        <h2>AI音乐生成的技术原理</h2>
        <p>现代AI音乐生成系统主要基于深度学习模型，特别是Transformer架构。与传统的RNN和LSTM相比，Transformer的并行计算能力和长程依赖处理能力有了质的飞跃。在我们的实践中，我们采用了改进版的Music Transformer模型，通过以下几个关键改进提升了生成质量：</p>
        
        <ul>
          <li>引入了相对位置编码，更好地捕捉音乐的时序特征</li>
          <li>设计了多层次的注意力机制，分别处理旋律、和声和节奏</li>
          <li>实现了条件控制机制，允许用户指定音乐风格和情感</li>
        </ul>

        <h2>实现挑战与解决方案</h2>
        <p>在开发过程中，我们遇到了诸多挑战。首先是模型的实时性问题，原始Transformer模型在生成长序列时计算开销较大。为此，我们采用了以下优化策略：</p>

        <div class="code-block">
          <pre><code>
// 优化后的生成核心代码
class MusicGenerator {
  constructor() {
    this.cache = new AttentionCache();
    this.streamBuffer = new CircularBuffer(1024);
  }

  async generateStream(prompt, params) {
    const batchSize = 32;  // 批处理优化
    let context = this.tokenizer.encode(prompt);
    
    while (true) {
      const predictions = await this.model.forward(context);
      const nextTokens = this.sample(predictions, params);
      
      // 流式输出优化
      this.streamBuffer.push(nextTokens);
      yield this.decoder.decode(nextTokens);
      
      // 注意力缓存优化
      this.cache.update(context, nextTokens);
      context = this.cache.getLastTokens(1024);
    }
  }
}
          </code></pre>
        </div>

        <h2>音乐表现力的突破</h2>
        <p>传统AI音乐常被诟病缺乏情感表达和音乐性。针对这个问题，我们开发了情感建模系统，通过分析大量人类演奏数据，提取出音乐表现力的关键特征：</p>

        <ul>
          <li>力度变化（Dynamics）：精确控制音符的强弱变化</li>
          <li>时值处理（Timing）：自然的节奏摇摆感</li>
          <li>音色变化（Timbre）：丰富的音色表现力</li>
        </ul>

        <h2>商业化落地思考</h2>
        <p>AI音乐的商业化应用是一个极具挑战性的课题。我们的平台采用了分层设计：</p>

        <ol>
          <li>基础层：提供API接口，支持基本的音乐生成功能</li>
          <li>应用层：针对不同场景开发特定解决方案
            <ul>
              <li>广告配乐生成器</li>
              <li>游戏背景音乐实时生成</li>
              <li>个人创作辅助工具</li>
            </ul>
          </li>
          <li>生态层：建立创作者社区，促进AI与人类创作者的协作</li>
        </ol>

        <h2>未来展望</h2>
        <p>展望未来，AI音乐生成技术还有很大的发展空间。我们正在研究的方向包括：</p>

        <ul>
          <li>多模态融合：将视觉、文本等多模态信息融入音乐生成过程</li>
          <li>实时互动：开发能够实时响应用户行为的音乐生成系统</li>
          <li>风格迁移：在保持音乐核心结构的同时，实现不同音乐风格间的自然转换</li>
        </ul>

        <p>作为技术从业者，我们始终相信AI不是要替代人类创作者，而是要为创作者提供更强大的工具，释放更多创造力。在这个快速发展的领域，保持开放和创新的心态至关重要。</p>

        <p>如果您对AI音乐感兴趣，欢迎访问我们的平台体验或参与开发讨论。让我们一起探索AI音乐的无限可能！</p>
      </div>

      <div class="article-footer">
        <div class="share-buttons">
          <button class="share-btn">
            <i class="fab fa-weixin"></i>
          </button>
          <button class="share-btn">
            <i class="fab fa-weibo"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ArticleDetail',
  data() {
    return {
      isNavOpen: false
    }
  },
  props: {
    id: {
      type: String,
      required: true
    }
  },
  methods: {
    toggleNav() {
      this.isNavOpen = !this.isNavOpen
    },
    closeNav() {
      this.isNavOpen = false
    }
  },
  created() {
    console.log('Article ID:', this.id)
    // 这里可以根据 ID 加载具体文章内容
  }
}
</script>

<style scoped>
/* 基础布局 */
.article-container {
  min-height: 100vh;
  padding: 2rem;
  background: var(--background-color);
}

.article-detail {
  margin: 80px auto;
  padding: 3rem;
  background: var(--card-bg);
  border-radius: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
}

/* 返回按钮 */
.back-button {
  position: fixed;
  top: 2rem;
  left: 2rem;
  z-index: 1000;
  padding: 0.5rem 1rem;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  border-radius: 2rem;
}

.back-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
}

.back-link:hover {
  color: var(--accent-color);
  transform: translateX(-5px);
}

/* 文章头部 */
.article-header {
  margin-bottom: 4rem;
  text-align: center;
  padding-bottom: 3rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.article-title {
  font-size: 2.5rem;
  margin: 1.5rem 0;
  line-height: 1.3;
  background: linear-gradient(45deg, var(--primary-color), var(--accent-color));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.article-meta {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

/* 标签样式 */
.article-tags {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: center;
}

.article-tags span {
  background: rgba(0, 255, 213, 0.1);
  color: var(--primary-color);
  padding: 0.3rem 0.8rem;
  border-radius: 2rem;
  font-size: 0.8rem;
  transition: all 0.3s ease;
}

.article-tags span:hover {
  background: rgba(0, 255, 213, 0.2);
  transform: translateY(-2px);
}

/* 作者信息 */
.article-author {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
}

.author-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: 2px solid var(--primary-color);
  transition: all 0.3s ease;
}

.author-avatar:hover {
  transform: scale(1.1);
  border-color: var(--accent-color);
}

/* 文章内容 */
.article-content {
  line-height: 1.8;
  margin: 2rem 0;
}

.article-content h2 {
  color: var(--primary-color);
  margin: 3rem 0 1.5rem;
  font-size: 1.8rem;
  border-bottom: 2px solid rgba(0, 255, 213, 0.1);
  padding-bottom: 0.5rem;
}

.article-content p {
  margin: 1.5rem 0;
  font-size: 1.1rem;
  color: var(--text-color);
}

.article-content ul,
.article-content ol {
  margin: 1.5rem 0;
  padding-left: 2rem;
}

.article-content li {
  margin: 0.8rem 0;
}

/* 代码块样式 */
.code-block {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 0.8rem;
  padding: 1.5rem;
  margin: 2rem 0;
  border: 1px solid var(--primary-color);
}

.code-block pre {
  margin: 0;
  overflow-x: auto;
}

.code-block code {
  color: var(--primary-color);
  font-family: 'Fira Code', monospace;
  font-size: 0.9rem;
  line-height: 1.6;
}

/* 文章底部 */
.article-footer {
  margin-top: 4rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.share-buttons {
  display: flex;
  justify-content: center;
  gap: 1rem;
}

.share-btn {
  background: none;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
}

.share-btn:hover {
  background: var(--primary-color);
  color: var(--background-color);
  transform: translateY(-3px);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .article-container {
    padding: 1rem;
  }

  .article-detail {
    padding: 2rem;
    margin: 60px auto;
  }

  .back-button {
    top: 1rem;
    left: 1rem;
  }

  .article-title {
    font-size: 2rem;
  }

  .article-content h2 {
    font-size: 1.5rem;
  }

  .article-content p {
    font-size: 1rem;
  }

  .code-block {
    padding: 1rem;
    margin: 1.5rem -1rem;
    border-radius: 0;
  }
}

/* 深色主题优化 */
@media (prefers-color-scheme: dark) {
  .article-detail {
    box-shadow: 0 4px 24px rgba(0, 255, 213, 0.05);
  }
}

/* 导航栏样式 */
.nav-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(10, 10, 10, 0.8);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--primary-color);
}

.nav-links {
  display: flex;
  justify-content: center;
  padding: 1rem;
  gap: 2rem;
}

.nav-links a {
  color: var(--primary-color);
  text-decoration: none;
  font-family: 'Fira Code', monospace;
  transition: all 0.3s ease;
}

.nav-links a:hover {
  color: var(--accent-color);
  text-shadow: 0 0 8px var(--accent-color);
}

/* 移动端汉堡菜单 */
.mobile-nav-toggle {
  display: none;
  position: fixed;
  top: 1rem;
  right: 1rem;
  z-index: 1001;
  background: none;
  border: none;
  cursor: pointer;
}

.hamburger {
  width: 30px;
  height: 25px;
  position: relative;
}

.hamburger span {
  display: block;
  position: absolute;
  height: 2px;
  width: 100%;
  background: var(--primary-color);
  transition: 0.3s all ease;
}

.hamburger span:nth-child(1) { top: 0; }
.hamburger span:nth-child(2) { top: 50%; transform: translateY(-50%); }
.hamburger span:nth-child(3) { bottom: 0; }

.hamburger.active span:nth-child(1) {
  transform: rotate(45deg);
  top: 50%;
}

.hamburger.active span:nth-child(2) {
  opacity: 0;
}

.hamburger.active span:nth-child(3) {
  transform: rotate(-45deg);
  bottom: 40%;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .mobile-nav-toggle {
    display: block;
  }

  .nav-bar {
    transform: translateY(-100%);
    transition: transform 0.3s ease;
  }

  .nav-bar.nav-open {
    transform: translateY(0);
  }

  .nav-links {
    background: var(--card-bg);
    padding: 1rem;
    margin-top: 60px;
    flex-direction: column;
    align-items: center;
  }

  .nav-links a {
    width: 100%;
    text-align: center;
    padding: 0.5rem;
    border: 1px solid rgba(0, 255, 213, 0.1);
    border-radius: 4px;
    background: rgba(0, 255, 213, 0.05);
  }

  .nav-links a:last-child {
    border-bottom: none;
  }
}

/* 雪花动画 */
.snowflakes {
  position: fixed;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 1;
}

.snowflake {
  position: absolute;
  color: var(--primary-color);
  opacity: 0.3;
  font-size: 1.5em;
  animation: fall linear infinite;
}

@keyframes fall {
  0% { transform: translateY(-100vh) rotate(0deg); }
  100% { transform: translateY(100vh) rotate(360deg); }
}

/* 为每个雪花设置不同的位置和动画时间 */
.snowflake:nth-child(1) { left: 5%; animation-duration: 15s; animation-delay: -1s; }
.snowflake:nth-child(2) { left: 10%; animation-duration: 12s; animation-delay: -2s; }
.snowflake:nth-child(3) { left: 15%; animation-duration: 13s; animation-delay: -3s; }
.snowflake:nth-child(4) { left: 20%; animation-duration: 14s; animation-delay: -4s; }
.snowflake:nth-child(5) { left: 25%; animation-duration: 16s; animation-delay: -5s; }
.snowflake:nth-child(6) { left: 30%; animation-duration: 11s; animation-delay: -6s; }
.snowflake:nth-child(7) { left: 35%; animation-duration: 17s; animation-delay: -7s; }
.snowflake:nth-child(8) { left: 40%; animation-duration: 13s; animation-delay: -8s; }
.snowflake:nth-child(9) { left: 45%; animation-duration: 14s; animation-delay: -9s; }
.snowflake:nth-child(10) { left: 50%; animation-duration: 15s; animation-delay: -10s; }
.snowflake:nth-child(11) { left: 55%; animation-duration: 12s; animation-delay: -11s; }
.snowflake:nth-child(12) { left: 60%; animation-duration: 16s; animation-delay: -12s; }
.snowflake:nth-child(13) { left: 65%; animation-duration: 13s; animation-delay: -13s; }
.snowflake:nth-child(14) { left: 70%; animation-duration: 14s; animation-delay: -14s; }
.snowflake:nth-child(15) { left: 75%; animation-duration: 15s; animation-delay: -15s; }
.snowflake:nth-child(16) { left: 80%; animation-duration: 11s; animation-delay: -16s; }
.snowflake:nth-child(17) { left: 85%; animation-duration: 17s; animation-delay: -17s; }
.snowflake:nth-child(18) { left: 90%; animation-duration: 13s; animation-delay: -18s; }
.snowflake:nth-child(19) { left: 95%; animation-duration: 14s; animation-delay: -19s; }
.snowflake:nth-child(20) { left: 100%; animation-duration: 15s; animation-delay: -20s; }
.snowflake:nth-child(21) { left: 7%; animation-duration: 16s; animation-delay: -21s; }
.snowflake:nth-child(22) { left: 13%; animation-duration: 12s; animation-delay: -22s; }
.snowflake:nth-child(23) { left: 17%; animation-duration: 13s; animation-delay: -23s; }
.snowflake:nth-child(24) { left: 23%; animation-duration: 14s; animation-delay: -24s; }
.snowflake:nth-child(25) { left: 27%; animation-duration: 15s; animation-delay: -25s; }
.snowflake:nth-child(26) { left: 33%; animation-duration: 16s; animation-delay: -26s; }
.snowflake:nth-child(27) { left: 37%; animation-duration: 12s; animation-delay: -27s; }
.snowflake:nth-child(28) { left: 43%; animation-duration: 13s; animation-delay: -28s; }
.snowflake:nth-child(29) { left: 47%; animation-duration: 14s; animation-delay: -29s; }
.snowflake:nth-child(30) { left: 53%; animation-duration: 15s; animation-delay: -30s; }
.snowflake:nth-child(31) { left: 57%; animation-duration: 16s; animation-delay: -31s; }
.snowflake:nth-child(32) { left: 63%; animation-duration: 12s; animation-delay: -32s; }
.snowflake:nth-child(33) { left: 67%; animation-duration: 13s; animation-delay: -33s; }
.snowflake:nth-child(34) { left: 73%; animation-duration: 14s; animation-delay: -34s; }
.snowflake:nth-child(35) { left: 77%; animation-duration: 15s; animation-delay: -35s; }
.snowflake:nth-child(36) { left: 83%; animation-duration: 16s; animation-delay: -36s; }
.snowflake:nth-child(37) { left: 87%; animation-duration: 12s; animation-delay: -37s; }
.snowflake:nth-child(38) { left: 93%; animation-duration: 13s; animation-delay: -38s; }
.snowflake:nth-child(39) { left: 97%; animation-duration: 14s; animation-delay: -39s; }
.snowflake:nth-child(40) { left: 3%; animation-duration: 15s; animation-delay: -40s; }
.snowflake:nth-child(41) { left: 8%; animation-duration: 16s; animation-delay: -41s; }
.snowflake:nth-child(42) { left: 12%; animation-duration: 12s; animation-delay: -42s; }
.snowflake:nth-child(43) { left: 18%; animation-duration: 13s; animation-delay: -43s; }
.snowflake:nth-child(44) { left: 22%; animation-duration: 14s; animation-delay: -44s; }
.snowflake:nth-child(45) { left: 28%; animation-duration: 15s; animation-delay: -45s; }
.snowflake:nth-child(46) { left: 32%; animation-duration: 16s; animation-delay: -46s; }
.snowflake:nth-child(47) { left: 38%; animation-duration: 12s; animation-delay: -47s; }
.snowflake:nth-child(48) { left: 42%; animation-duration: 13s; animation-delay: -48s; }
.snowflake:nth-child(49) { left: 48%; animation-duration: 14s; animation-delay: -49s; }
.snowflake:nth-child(50) { left: 52%; animation-duration: 15s; animation-delay: -50s; }

@media (max-width: 768px) {
  .snowflake {
    font-size: 1em;
  }
}
</style> 